import { useToast } from '@chakra-ui/react'
import { captureException } from '@sentry/react'
import type React from 'react'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import Copy01Icon from '../../../../../assets/icons/copy-01-stroke-rounded'
import PageWrapper from '../../../../../components/Pagewrapper'
import Table from '../../../../../components/Table/Table'
import Toast from '../../../../../components/Toast'
import TransferStatusBadge from '../../../../../components/TransferStatusBadge'
import { INVESTMENT_PACKAGE_LABELS } from '../../../../../constants/investmentPackages'
import { useGetAllAdminFundDepositsQuery } from '../../../../../redux/services/coreApi'
import { setFundDepositsTableState } from '../../../../../redux/slices/tableFiltersSlice'
import { RootState } from '../../../../../redux/store'
import { AdminFundDepositDto } from '../../../../../types/coreApi-types'
import { downloadFundDepositInvoice } from '../../../../../utils/downloadInvoice'
import { formatNumber } from '../../../../../utils/formatters'
import FundDepositSummaryModal from './components/FundDepositSummaryModal'

const BookkeeperFundDepositsScreen: React.FC = () => {
  const toast = useToast()
  const sessionToken = useSelector((state: RootState) => state.session.token)

  const { data: depositsData, isLoading } = useGetAllAdminFundDepositsQuery()

  const [activeDeposit, setActiveDeposit] =
    useState<AdminFundDepositDto | null>(null)
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false)

  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState<{
    [depositId: string]: boolean
  }>({})

  const openSummaryModal = (v: AdminFundDepositDto) => {
    setActiveDeposit(v)
    setIsSummaryModalOpen(true)
  }

  const fundDepositsTableState = useSelector(
    (state: RootState) => state.fundDepositsTable
  )

  return (
    <PageWrapper title="Portfolio Deposits">
      <Table
        tableState={fundDepositsTableState}
        setTableState={setFundDepositsTableState}
        navigate={() => null}
        selectMenuActions={[
          {
            title: 'Download receipts',
            icon: 'download',
            selected: values => values,
            onClick: async rows => {
              const typedRows = rows as { original: AdminFundDepositDto }[]
              await Promise.all(
                typedRows.map(async row => {
                  const deposit = row.original
                  if (deposit.transferStatus === 'Completed') {
                    try {
                      await downloadFundDepositInvoice(deposit, sessionToken)
                    } catch (e) {
                      captureException(e)
                    }
                  }
                })
              )
              return rows
            },
            hidden: _values => false,
          },
        ]}
        isLoading={isLoading}
        onClickRow={v => openSummaryModal(v)}
        emptyDataRender={
          <div className="text-xl mb-3">No portfolio deposits yet.</div>
        }
        rowMenuActions={[
          {
            title: 'View order details',
            onClick: v => openSummaryModal(v),
            hidden: () => false,
          },
          {
            title: 'Download receipt',
            onClick: async v => {
              setIsDownloadingInvoice(prev => ({
                ...prev,
                [v.id]: true,
              }))

              try {
                await downloadFundDepositInvoice(v, sessionToken)
              } catch (e) {
                captureException(e)
              } finally {
                setIsDownloadingInvoice(prev => ({
                  ...prev,
                  [v.id]: false,
                }))
              }
            },
            isLoading: v => isDownloadingInvoice[v.id],
            disabled: v => isDownloadingInvoice[v.id],
            hidden: v => v.transferStatus !== 'Completed',
          },
        ]}
        defaultData={[
          ...(depositsData?.map(item => {
            return {
              ...item,
              name: item.user.name,
              status: item.transferStatus,
              bankDepositUniqueId: item.bankDepositUniqueId,
              packageLabel: item.investmentPackage
                ? INVESTMENT_PACKAGE_LABELS[item.investmentPackage]
                : '',
              depositAmount: formatNumber(item.amount, item.currency),
            }
          }) ?? []),
        ]}
        schema={[
          {
            header: 'Status',
            accessorKey: 'status',
            type: 'status',
            priority: 1,
            cellValueWrapper: (value, originalValue, { original }) => (
              <div className="w-fit min-w-[120px]">
                {original?.status && (
                  <TransferStatusBadge transferStatus={original.status} />
                )}
              </div>
            ),
          },
          {
            cardTitle: true,
            header: 'Date created',
            accessorKey: 'createdAt',
            type: 'date',
            priority: 2,
            initialSorted: true,
          },
          {
            header: 'Investor name',
            accessorKey: 'name',
            type: 'string',
            priority: 3,
          },
          {
            header: 'Package',
            accessorKey: 'packageLabel',
            type: 'string',
            priority: 4,
          },
          {
            header: 'Amount',
            accessorKey: 'depositAmount',
            type: 'string',
            priority: 5,
          },
          {
            header: 'Bank Deposit Id',
            accessorKey: 'bankDepositUniqueId',
            type: 'string',
            priority: 6,
            cellValueWrapper: (value, originalValue, { original }) =>
              original?.bankDepositUniqueId ? (
                <div
                  className="px-2 py-1.5 w-min hover:bg-gray-100 transition-all rounded-md group"
                  onClick={e => {
                    e.stopPropagation()
                    navigator.clipboard.writeText(original.bankDepositUniqueId!)

                    toast({
                      position: 'top',
                      render: () => {
                        return <Toast type="success">Id copied</Toast>
                      },
                    })
                  }}
                >
                  <div className="flex gap-1.5 items-center">
                    {original.bankDepositUniqueId}
                    <div className="group-hover:opacity-100 opacity-0 transition-all">
                      <Copy01Icon className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              ) : (
                <></>
              ),
          },
        ]}
      />
      <FundDepositSummaryModal
        isOpen={isSummaryModalOpen}
        onClose={() => setIsSummaryModalOpen(false)}
        deposit={activeDeposit}
      />
    </PageWrapper>
  )
}

export default BookkeeperFundDepositsScreen
